import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2 } from 'lucide-react';
import Navbar from '../../components/brand/Navbar';
import Footer from '../../components/brand/Footer';
import FAQSection from '../../components/brand/FAQSection';

const faqs = [
  {
    q: 'How does mobile pathology collection work in an aged care facility?',
    a: 'We schedule a regular visit day with your facility. On that day, a qualified collector arrives and works with your nursing staff to complete all resident blood collections on-site. No transport, no escorts, no disruption to your care schedule. Specimens are transported to our partner laboratory and results go directly to each resident\'s GP.',
  },
  {
    q: 'Do we need to provide a room or space for the collector?',
    a: 'A small, private area where the collector can work with each resident is ideal — this could be a treatment room, consultation room, or even a resident\'s own room. The collector brings all equipment, consumables, and sharps disposal. No special setup is required from your facility.',
  },
  {
    q: 'How does billing work for facilities?',
    a: 'Facilities with 15 or more residents per visit receive a consolidated monthly invoice. Smaller facilities are invoiced per visit. We do not bill individual residents — the facility manages the relationship. There are no lock-in contracts.',
  },
  {
    q: 'Is mobile collection as reliable as sending residents to a pathology centre?',
    a: 'Yes. Our collectors hold the same Certificate III in Pathology Collection (HLT37215) as collectors at any walk-in centre. Our procedures are aligned to the NPAAC 5th Edition, the national standard that governs all pathology collection in Australia. Specimens are transported in temperature-controlled conditions with documented chain of custody.',
  },
  {
    q: "What happens if a resident's veins are difficult to access?",
    a: "Our collectors are experienced in working with elderly patients and are trained in techniques for difficult venous access. If a collection cannot be completed, the collector will document the attempt and work with your nursing staff to determine next steps, which may include rescheduling or referring to a centre with additional equipment.",
  },
  {
    q: 'Can we start with a trial before committing to regular visits?',
    a: 'Absolutely. Most facilities start with a single trial visit so you can see how the process works in your environment. There is no cost difference for a trial — you pay the same per-resident rate. Contact us on 0420 424 290 or email hello@nurahealth.com.au to arrange a trial.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const Section = ({ id, title, children }) => (
  <section
    id={id}
    className="py-16"
    style={{ borderBottom: '1px solid rgba(43, 56, 121, 0.08)' }}
  >
    <div className="container max-w-4xl mx-auto px-6">
      <h2
        className="font-['Inter'] font-bold mb-5 text-[#00001E]"
        style={{ fontSize: '1.6rem', letterSpacing: '-0.02em' }}
      >
        {title}
      </h2>
      <div className="text-[#2B3879] leading-relaxed" style={{ fontSize: '1rem' }}>
        {children}
      </div>
    </div>
  </section>
);

const InternalLink = ({ href, children }) => (
  <a href={href} className="text-[#2B3879] hover:underline font-semibold">{children}</a>
);

export default function AgedCarePathology() {
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'view_service_page', {
        event_category: 'engagement',
        event_label: 'Aged Care Pathology'
      });
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Aged Care Pathology Services | Mobile Blood Collection Brisbane, Sydney &amp; Melbourne | Nura Health</title>
        <meta name="description" content="On-site blood collection for aged care facilities across Brisbane, Sydney and Melbourne metros. Reduce escort costs, maintain care-minute compliance. NPAAC-compliant mobile pathology." />
        <link rel="canonical" href="https://nurahealth.com.au/services/aged-care-pathology" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen" style={{ background: '#F7F9FB' }}>
        <Navbar onBookClick={() => (window.location.href = '/booking')} />

        {/* Hero — dark banner */}
        <section
          className="pt-36 pb-20 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #00001E 0%, #0a0b2e 60%, #101a3e 100%)' }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at 20% 60%, rgba(128,229,203,0.09) 0%, transparent 55%)',
          }} />
          <div className="container max-w-4xl mx-auto px-6 relative z-10">
            <span className="pill-label-dark">Aged Care</span>
            <h1
              className="font-['Inter'] font-extrabold mb-5"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.03em', color: '#FFFFFF' }}
            >
              Mobile Pathology Collection for Aged Care Facilities
            </h1>
            <p className="text-lg mb-8 max-w-2xl leading-relaxed" style={{ color: 'rgba(255,255,255,0.80)' }}>
              On-site blood collection that eliminates escort hours, reduces costs, and keeps your residents comfortable.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="/booking"
                style={{
                  display: 'inline-block',
                  background: '#80E5CB',
                  color: '#00001E',
                  borderRadius: '8px',
                  padding: '13px 28px',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  boxShadow: '0 4px 16px rgba(128,229,203,0.35)',
                }}
              >
                Request a Visit
              </a>
              <a
                href="tel:0420424290"
                style={{
                  display: 'inline-block',
                  background: 'transparent',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '12px 26px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  border: '2px solid rgba(255,255,255,0.40)',
                }}
              >
                Call 0420 424 290
              </a>
            </div>
          </div>
        </section>

        <Section title="The Hidden Cost of Off-Site Pathology Appointments">
          <p>
            Every time a resident needs blood work at an external pathology centre, your facility must provide a staff escort.
            That means pulling a carer off the floor, arranging transport, and losing productive care minutes — all for a routine
            blood draw that could be done on-site. For facilities managing dozens of residents with regular pathology needs,
            these escort hours add up to a significant operational and compliance burden.
          </p>
        </Section>

        <Section title="How On-Site Collection Works">
          <p>
            Nura Health sends a qualified mobile phlebotomist directly to your facility on a scheduled day. We work with your
            nursing staff to coordinate resident collections efficiently — typically completing 10 to 20 collections in a single
            visit. Specimens are handled and transported in full compliance with NPAAC 5th Edition standards. Results are sent
            to each resident's GP exactly as they would be from a traditional collection centre. No disruption to your care
            schedule, no escort staffing, no transport coordination.
          </p>
        </Section>

        <Section title="Volume-Based Pricing for Facilities">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {[
              { range: '1–4 residents', price: '$55 / resident' },
              { range: '5–14 residents', price: '$50 / resident' },
              { range: '15–29 residents', price: '$45 / resident' },
              { range: '30+ residents', price: '$40 / resident' },
            ].map(({ range, price }) => (
              <div
                key={range}
                className="rounded-xl p-5 text-center bg-white"
                style={{ border: '1px solid rgba(43,56,121,0.12)', boxShadow: '0 2px 12px rgba(43,56,121,0.06)' }}
              >
                <p className="text-[#00001E] font-semibold text-lg">{price}</p>
                <p className="text-[#2B3879] text-sm mt-1">{range}</p>
              </div>
            ))}
          </div>
          <p>
            Facilities with 15 to 29 residents pay on a consolidated monthly invoice. For 30 or more residents,
            you receive a dedicated collector and priority scheduling. No lock-in contracts — we earn your ongoing
            business through reliable service.
          </p>
        </Section>

        <Section title="Compliance and Credentials">
          <ul className="space-y-3">
            {[
              'Certificate III in Pathology Collection (HLT37215) — every collector',
              '$10 million+ professional indemnity insurance',
              'Current police checks for all staff',
              'NPAAC 5th Edition compliant internal SOP (NH-SOP-001 v1.0)',
              'Timestamped collection logs and chain-of-custody documentation',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#2B3879] flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Request a Trial Visit">
          <p className="mb-6">
            See how mobile pathology collection works in your facility. Contact us to arrange a no-obligation trial visit.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="/booking"
              style={{
                display: 'inline-block',
                background: '#80E5CB',
                color: '#00001E',
                borderRadius: '8px',
                padding: '13px 28px',
                fontWeight: 700,
                fontSize: '0.95rem',
                textDecoration: 'none',
              }}
            >
              Book a Facility Visit
            </a>
            <a
              href="tel:0420424290"
              style={{
                display: 'inline-block',
                background: 'transparent',
                color: '#2B3879',
                borderRadius: '8px',
                padding: '12px 26px',
                fontWeight: 600,
                fontSize: '0.95rem',
                textDecoration: 'none',
                border: '2px solid #2B3879',
              }}
            >
              Call 0420 424 290
            </a>
          </div>
        </Section>

        <FAQSection items={faqs} />

        <section className="py-12 bg-[#F7F9FB]">
          <div className="container max-w-4xl mx-auto px-6">
            <p className="text-[#2B3879] text-sm">
              Also see:{' '}
              <InternalLink href="/services/ndis-pathology">NDIS pathology collection</InternalLink>
              {' · '}
              <InternalLink href="/services/home-blood-collection">home blood collection</InternalLink>
              {' · '}
              <InternalLink href="/">back to home</InternalLink>
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
